import {
  LARGE_IMAGE_BLOCK_SIZE,
  LARGE_IMAGE_THRESHOLD,
  MEDIUM_IMAGE_BLOCK_SIZE,
  MEDIUM_IMAGE_THRESHOLD,
  SMALL_IMAGE_BLOCK_SIZE,
} from '../../constants';
import type { Coordinates, TextBoundaries } from '../TextParticles.types';

export const OPACITY_THRESHOLD = 10;

// Returns coordinates for not transparent parts of the image
export const getValidImageCoordinates = (
  imageData: ImageData,
  particleSize: number,
) => {
  const { width, height, data } = imageData;
  const validImageCoordinates: Array<Coordinates> = [];

  // Check if any pixel opacity within block is larger than OPACITY_THRESHOLD
  const isValidImageBlock = (blockX: number, blockY: number) => {
    for (let pixelY = 0; pixelY < particleSize; pixelY++) {
      for (let pixelX = 0; pixelX < particleSize; pixelX++) {
        const srcX = blockX + pixelX;
        const srcY = blockY + pixelY;
        if (srcX < width && srcY < height) {
          const srcIndex = (srcY * width + srcX) * 4;
          if (data[srcIndex + 3] > OPACITY_THRESHOLD) {
            return true;
          }
        }
      }
    }
    return false;
  };

  for (let y = 0; y < height; y += particleSize) {
    for (let x = 0; x < width; x += particleSize) {
      if (isValidImageBlock(x, y)) {
        validImageCoordinates.push({ x, y });
      }
    }
  }

  return validImageCoordinates;
};

// After transferring image bitmap to worker context, main thread can still have a reference to image
// but its value is in the worker context https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Transferable_objects
export const isImageTransferred = (imageBitmap: ImageBitmap) =>
  imageBitmap && imageBitmap.width === 0 && imageBitmap.height === 0;

export const calculateContentBounds = (
  imageData: ImageData,
  padding: number = 0,
): TextBoundaries => {
  const { width, height, data } = imageData;
  let minX = width;
  let maxX = -1;
  let minY = height;
  let maxY = -1;

  // Optimized scanning: find top and bottom bounds first
  let foundContent = false;

  // Scan from top to find minY
  for (let y = 0; y < height && minY === height; y++) {
    for (let x = 0; x < width; x++) {
      const pixelIndex = (y * width + x) * 4;
      if (data[pixelIndex + 3] > 0) {
        minY = y;
        foundContent = true;
        break;
      }
    }
  }

  if (!foundContent) {
    return { minX: 0, maxX: width, minY: 0, maxY: height, width, height };
  }

  // Scan from bottom to find maxY
  for (let y = height - 1; y >= 0 && maxY === -1; y--) {
    for (let x = 0; x < width; x++) {
      const pixelIndex = (y * width + x) * 4;
      if (data[pixelIndex + 3] > 0) {
        maxY = y;
        break;
      }
    }
  }

  // Scan from left to find minX (only within Y bounds)
  for (let x = 0; x < width && minX === width; x++) {
    for (let y = minY; y <= maxY; y++) {
      const pixelIndex = (y * width + x) * 4;
      if (data[pixelIndex + 3] > 0) {
        minX = x;
        break;
      }
    }
  }

  // Scan from right to find maxX (only within Y bounds)
  for (let x = width - 1; x >= 0 && maxX === -1; x--) {
    for (let y = minY; y <= maxY; y++) {
      const pixelIndex = (y * width + x) * 4;
      if (data[pixelIndex + 3] > 0) {
        maxX = x;
        break;
      }
    }
  }

  minX -= padding;
  maxX += padding;
  minY -= padding;
  maxY += padding;

  return { minX, maxX, minY, maxY, width: maxX - minX, height: maxY - minY };
};

export const getBlockSize = ({
  devicePixelRatio,
  contentWidth,
  contentHeight,
}: {
  devicePixelRatio: number;
  contentWidth: number;
  contentHeight: number;
}) => {
  const imageArea = contentWidth * contentHeight * devicePixelRatio;

  if (imageArea < MEDIUM_IMAGE_THRESHOLD) {
    return SMALL_IMAGE_BLOCK_SIZE;
  }
  if (imageArea < LARGE_IMAGE_THRESHOLD) {
    return MEDIUM_IMAGE_BLOCK_SIZE;
  }

  return LARGE_IMAGE_BLOCK_SIZE;
};
